import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2 } from "lucide-react";
import { WaveformPlayer } from "./Waveform";
import { BRAND } from "../data/content";

/**
 * Audio player with a CSS waveform. Uses an HTMLAudioElement under the hood.
 * Defaults to the official "Talking Trails Opening" track supplied by the brand.
 */
const SAMPLE_TRACK = BRAND.openingAudio;

function fmt(t) {
  if (!Number.isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({
  stop,
  src = SAMPLE_TRACK,
  index = 1,
  className = "",
}) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (playing) {
        a.pause();
        setPlaying(false);
      } else {
        await a.play();
        setPlaying(true);
      }
    } catch {
      setPlaying(false);
    }
  };

  const progress = duration > 0 ? (current / duration) * 100 : 0;

  return (
    <div
      data-testid={`audio-player-${index}`}
      className={`relative bg-brand-cream border border-brand-line/60 p-6 md:p-8 ${className}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Stop {String(index).padStart(2, "0")}</div>
          <h4 className="font-serif text-2xl md:text-3xl text-brand-charcoal mt-1.5">
            {stop?.title || "Sample story"}
          </h4>
          {stop?.subtitle && (
            <p className="text-sm text-brand-muted mt-1 italic">{stop.subtitle}</p>
          )}
        </div>
        <button
          data-testid={`audio-play-${index}`}
          onClick={toggle}
          className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-brand-rainforest text-brand-cream flex items-center justify-center hover:bg-brand-rainforestDark transition-colors duration-300"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
        </button>
      </div>

      <div className="mt-6">
        <WaveformPlayer playing={playing} progress={progress} />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-brand-muted tracking-wide">
        <span data-testid={`audio-current-${index}`}>{fmt(current)}</span>
        <div className="flex items-center gap-2 text-brand-muted/80">
          <Volume2 size={14} />
          <span>{stop?.duration || fmt(duration)}</span>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
}
